import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import * as api from "../lib/api";

export default function AccountSheet({
  open,
  onOpenChange,
  onLogoutAfterDelete,
  isAuthenticated,
}) {
  const [loading, setLoading] = useState(false);

  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyTracesEnabled, setNotifyTracesEnabled] = useState(true);
  const [notifyBroadcastEnabled, setNotifyBroadcastEnabled] = useState(true);
  const [notifyLat, setNotifyLat] = useState(null);
  const [notifyLng, setNotifyLng] = useState(null);
  const [notifyRadius, setNotifyRadius] = useState(25);

  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const canDelete = useMemo(() => deleteConfirm.trim().toUpperCase() === "SUPPRIMER", [deleteConfirm]);

  const refresh = async () => {
    setLoading(true);
    try {
      const me = await api.getMe();
      setNotifyEnabled(!!me.notify_spots_enabled);
      setNotifyTracesEnabled(me.notify_traces_enabled ?? true);
      setNotifyBroadcastEnabled(me.notify_broadcast_enabled ?? true);
      setEmail(me.email || "");
      setNotifyLat(me.notify_center_lat ?? null);
      setNotifyLng(me.notify_center_lng ?? null);
      setNotifyRadius(me.notify_radius_km ?? 25);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger ton compte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && isAuthenticated) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  const handleToggleNotify = async () => {
    try {
      const next = !notifyEnabled;
      setNotifyEnabled(next);
      await api.updateMyNotifications({
        notify_spots_enabled: next,
        notify_traces_enabled: notifyTracesEnabled,
        notify_broadcast_enabled: notifyBroadcastEnabled,
        notify_center_lat: notifyLat,
        notify_center_lng: notifyLng,
        notify_radius_km: notifyRadius,
      });
      toast.success(next ? "Notifications activées" : "Notifications désactivées");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de mettre à jour les notifications");
      setNotifyEnabled((v) => !v);
    }
  };

  const handleToggleTraces = async () => {
    try {
      const next = !notifyTracesEnabled;
      setNotifyTracesEnabled(next);
      await api.updateMyNotifications({
        notify_spots_enabled: notifyEnabled,
        notify_traces_enabled: next,
        notify_broadcast_enabled: notifyBroadcastEnabled,
        notify_center_lat: notifyLat,
        notify_center_lng: notifyLng,
        notify_radius_km: notifyRadius,
      });
      toast.success(next ? "Notifications traces activées" : "Notifications traces désactivées");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de mettre à jour les notifications");
      setNotifyTracesEnabled((v) => !v);
    }
  };

  const handleToggleBroadcast = async () => {
    try {
      const next = !notifyBroadcastEnabled;
      setNotifyBroadcastEnabled(next);
      await api.updateMyNotifications({
        notify_spots_enabled: notifyEnabled,
        notify_traces_enabled: notifyTracesEnabled,
        notify_broadcast_enabled: next,
        notify_center_lat: notifyLat,
        notify_center_lng: notifyLng,
        notify_radius_km: notifyRadius,
      });
      toast.success(next ? "Emails d'info activés" : "Emails d'info désactivés");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de mettre à jour les notifications");
      setNotifyBroadcastEnabled((v) => !v);
    }
  };

  const handleSaveZone = async () => {
    if (notifyLat === null || notifyLng === null) {
      toast.error("Utilise d'abord le bouton pour récupérer ta position");
      return;
    }
    try {
      await api.updateMyNotifications({
        notify_spots_enabled: notifyEnabled,
        notify_traces_enabled: notifyTracesEnabled,
        notify_broadcast_enabled: notifyBroadcastEnabled,
        notify_center_lat: notifyLat,
        notify_center_lng: notifyLng,
        notify_radius_km: notifyRadius,
      });
      toast.success("Zone enregistrée");
    } catch (e) {
      toast.error("Impossible d'enregistrer la zone");
    }
  };

  const handleDeleteZone = async () => {
    try {
      await api.updateMyNotifications({
        notify_spots_enabled: notifyEnabled,
        notify_traces_enabled: notifyTracesEnabled,
        notify_broadcast_enabled: notifyBroadcastEnabled,
        notify_center_lat: null,
        notify_center_lng: null,
        notify_radius_km: null,
      });
      setNotifyLat(null);
      setNotifyLng(null);
      setNotifyRadius(25);
      toast.success("Zone supprimée");
    } catch (e) {
      toast.error("Impossible de supprimer la zone");
    }
  };

  const handleGetPosition = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non disponible sur ce navigateur");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setNotifyLat(coords.latitude);
        setNotifyLng(coords.longitude);
        toast.success("Position récupérée");
      },
      () => toast.error("Impossible de récupérer la position")
    );
  };

  const handleUpdateEmail = async () => {
    if (!email || !emailPassword) {
      toast.error("Email et mot de passe requis");
      return;
    }
    try {
      await api.updateMyEmail(email, emailPassword);
      setEmailPassword("");
      toast.success("Email mis à jour");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Impossible de changer l'email");
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Champs requis");
      return;
    }
    try {
      await api.updateMyPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Mot de passe mis à jour");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Impossible de changer le mot de passe");
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error("Mot de passe requis");
      return;
    }
    if (!canDelete) {
      toast.error('Tape "SUPPRIMER" pour confirmer');
      return;
    }
    try {
      await api.deleteMyAccount(deletePassword);
      toast.success("Compte supprimé");
      onOpenChange(false);
      onLogoutAfterDelete?.();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Impossible de supprimer le compte");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[420px] bg-[#0f0d0e] text-white border-white/10">
        <SheetHeader>
          <SheetTitle className="text-white">Mon compte</SheetTitle>
        </SheetHeader>

        <ScrollArea className="mt-4 h-[calc(100vh-80px)] pr-2">
          <div className="space-y-4">

            {/* Notifications */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/5 space-y-4">
              <h3 className="font-semibold text-white">Notifications</h3>

              <p className="text-sm text-white/60">
                📬 Pensez à ajouter{" "}
                <span className="text-white/80">no-reply@mail.urbsex.club</span>{" "}
                à vos contacts — les filtres anti-spam sont sans pitié.
              </p>

              {/* Toggle 1 — spots */}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/80">
                  Recevoir un email quand un spot est ajouté dans ma zone
                </span>
                <Button
                  size="sm"
                  variant={notifyEnabled ? "secondary" : "outline"}
                  className={notifyEnabled ? "" : "border-white/20 bg-white/5 text-white hover:bg-white/10"}
                  onClick={handleToggleNotify}
                  disabled={loading}
                >
                  {notifyEnabled ? "ON" : "OFF"}
                </Button>
              </div>

              {/* Toggle 2 — traces */}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/80">
                  Recevoir un email quand un utilisateur trace un de mes spots
                </span>
                <Button
                  size="sm"
                  variant={notifyTracesEnabled ? "secondary" : "outline"}
                  className={notifyTracesEnabled ? "" : "border-white/20 bg-white/5 text-white hover:bg-white/10"}
                  onClick={handleToggleTraces}
                  disabled={loading}
                >
                  {notifyTracesEnabled ? "ON" : "OFF"}
                </Button>
              </div>

              {/* Toggle 3 — broadcast */}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white/80">
                  Recevoir les emails d'information de l'équipe dev
                </span>
                <Button
                  size="sm"
                  variant={notifyBroadcastEnabled ? "secondary" : "outline"}
                  className={notifyBroadcastEnabled ? "" : "border-white/20 bg-white/5 text-white hover:bg-white/10"}
                  onClick={handleToggleBroadcast}
                  disabled={loading}
                >
                  {notifyBroadcastEnabled ? "ON" : "OFF"}
                </Button>
              </div>

              {/* Sous-section Ma zone */}
              <div className="pt-2 border-t border-white/10 space-y-3">
                <h4 className="font-medium text-white/90">Ma zone</h4>
                <p className="text-sm text-white/50">
                  Sans zone définie, tu reçois une notification pour chaque nouveau spot ajouté, où qu'il soit.
                </p>

                {notifyLat !== null && notifyLng !== null && (
                  <div className="flex items-center justify-between text-xs text-white/70 bg-white/5 rounded px-2 py-1.5">
                    <span>
                      Zone : {notifyLat.toFixed(4)}, {notifyLng.toFixed(4)} — {notifyRadius} km
                    </span>
                    <button
                      className="text-red-400 hover:text-red-300 ml-2"
                      onClick={handleDeleteZone}
                    >
                      Supprimer
                    </button>
                  </div>
                )}

                <div className="space-y-1">
                  <div className="text-xs text-white/50">Rayon</div>
                  <Select value={String(notifyRadius)} onValueChange={(v) => setNotifyRadius(Number(v))}>
                    <SelectTrigger className="bg-white/5 border-white/20 text-white text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f0d0e] text-white border-white/10">
                      <SelectItem value="10" className="focus:bg-white/10 focus:text-white">10 km</SelectItem>
                      <SelectItem value="25" className="focus:bg-white/10 focus:text-white">25 km</SelectItem>
                      <SelectItem value="50" className="focus:bg-white/10 focus:text-white">50 km</SelectItem>
                      <SelectItem value="100" className="focus:bg-white/10 focus:text-white">100 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 text-xs"
                  onClick={handleGetPosition}
                >
                  Utiliser ma position actuelle
                </Button>

                <Button
                  size="sm"
                  className="w-full bg-white text-black hover:bg-white/90 text-xs"
                  onClick={handleSaveZone}
                >
                  Enregistrer la zone
                </Button>
              </div>
            </div>

            {/* Modifier mot de passe */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/5 space-y-2">
              <div className="text-sm font-semibold">Modifier mon mot de passe</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-white/70">Mot de passe actuel</Label>
                  <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-white/70">Nouveau mot de passe</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </div>
                <Button onClick={handleUpdatePassword} disabled={loading} className="w-full">
                  Mettre à jour
                </Button>
              </div>
            </div>

            {/* Modifier email */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/5 space-y-2">
              <div className="text-sm font-semibold">Modifier mon email</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-white/70">Nouvel email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-white/70">Mot de passe</Label>
                  <Input type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} />
                </div>
                <Button onClick={handleUpdateEmail} disabled={loading} className="w-full">
                  Mettre à jour
                </Button>
              </div>
            </div>

            {/* Supprimer compte */}
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 space-y-2">
              <div className="text-sm font-semibold text-red-200">Supprimer mon compte</div>
              <div className="text-xs text-white/70">
                Tes traces seront supprimées. Tes spots restent sur la carte avec l'auteur indiqué comme "Profil inactif".
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-white/70">Mot de passe</Label>
                  <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-white/70">Tape "SUPPRIMER"</Label>
                  <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} />
                </div>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={loading || !canDelete}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  Supprimer définitivement
                </Button>
              </div>
            </div>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
